import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  PhoneCall,
  Home as HomeIcon,
  Sparkles,
  Stethoscope,
} from "lucide-react";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useSession } from "../context/SessionContext";
import { requestConsultation, photoUrl } from "../lib/api";

export default function Vision() {
  const navigate = useNavigate();
  const { session, clear } = useSession();
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [params] = useSearchParams();
  const manual = params.get("manual") === "1";

  if (!session) return <Navigate to="/" replace />;
  const needsAck = session.concept?.status === "complete" && !session.disclaimer_ack?.accepted;

  const isDiagnose = session.pathway === "diagnose";
  const toneBtn = isDiagnose
    ? "bg-sky-600 hover:bg-sky-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  const primary = (session.photos || []).find((p) => p.is_primary) || session.photos?.[0];

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await requestConsultation(session.id, notes.trim() || null, manual);
      setConfirmMsg(res.message);
      setConfirmed(true);
      if (res.email_status === "sent") {
        toast.success("Request sent to GreenFlow.");
      } else {
        toast.success("Request saved. GreenFlow will follow up shortly.");
      }
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  const goHome = () => {
    clear();
    navigate("/");
  };

  if (confirmed) {
    return (
      <div className="min-h-[100dvh] gf-gradient-dark gf-grain">
        <div className="mx-auto max-w-xl px-5 pt-16 pb-10 text-center gf-fade-in">
          <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center">
            <CheckCircle2 className="text-emerald-300" size={44} />
          </div>
          <h1
            data-testid="confirmation-heading"
            className="mt-6 font-display text-white text-3xl font-extrabold leading-tight"
          >
            Thank you.
          </h1>
          <p data-testid="confirmation-message" className="mt-3 text-emerald-100/90 text-base">
            {confirmMsg || "A GreenFlow specialist will contact you shortly."}
          </p>
          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4 text-left text-sm text-emerald-50/90">
            <div className="opacity-70 text-xs uppercase tracking-widest">
              On file
            </div>
            <div className="font-semibold mt-1">{session.customer.name}</div>
            <div>{session.customer.email}</div>
            <div>{session.customer.phone}</div>
            <div className="opacity-80 mt-1">{session.customer.address}</div>
            <div className="opacity-60 text-xs mt-2">
              Pathway: {isDiagnose ? "Diagnose My Landscape" : "Improve My Landscape"} ·
              Photos: {session.photos?.length || 0}
            </div>
          </div>
          <Button
            data-testid="confirmation-home-button"
            onClick={goHome}
            className="mt-8 w-full h-12 bg-white text-[#092C15] hover:bg-emerald-50 font-semibold rounded-xl"
          >
            <HomeIcon className="mr-2" size={18} /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]">
      <BrandHeader
        back
        title="Request a GreenFlow Consultation"
        subtitle={isDiagnose ? "Diagnose My Landscape" : "Improve My Landscape"}
      />
      <main className="mx-auto max-w-xl px-5 py-6 gf-fade-in">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-[11px] uppercase tracking-widest font-bold">
          {isDiagnose ? <Stethoscope size={12} /> : <Sparkles size={12} />}
          {isDiagnose ? "Diagnose" : "Improve"} · Consultation
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold text-[#092C15] leading-tight">
          Ready for GreenFlow to Take a Closer Look?
        </h1>
        <p className="mt-1 text-slate-600 text-sm">
          Confirm your details and request a consultation. Your photos and
          assessment are included automatically.
        </p>

        {/* Before / After placeholder */}
        <section className="mt-6 relative rounded-3xl overflow-hidden border border-slate-200 bg-slate-900">
          {primary ? (
            <>
              <img
                src={photoUrl(primary.id)}
                alt="Current property"
                className="w-full aspect-[16/10] object-cover"
              />
              <div className="absolute left-3 top-3 bg-black/60 text-white text-[11px] font-semibold rounded-full px-2 py-0.5">
                YOUR LANDSCAPE TODAY
              </div>
            </>
          ) : (
            <div className="aspect-[16/10] grid place-items-center text-slate-300 text-sm">
              No photo on file.
            </div>
          )}
        </section>

        {/* Request Estimate */}
        <section className="mt-6 rounded-3xl bg-white border border-emerald-200 p-5">
          <h3 className="font-display text-xl font-bold text-[#092C15]">
            Request Estimate / Schedule Consultation
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Add anything the specialist should know. Your name and property
            address are already on file.
          </p>

          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900">
            <div className="font-semibold">{session.customer.name}</div>
            <div>{session.customer.email} · {session.customer.phone}</div>
            <div className="opacity-80">{session.customer.address}</div>
          </div>

          {manual && (
            <div data-testid="manual-review-notice" className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              Your project will be sent to GreenFlow for a personal review of your photos and preferences.
            </div>
          )}
          {needsAck && (
            <div data-testid="vision-ack-required" className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              Please acknowledge the concept disclaimer on your results screen before requesting a consultation.
              <button className="ml-1 underline font-semibold" onClick={() => navigate("/improve")}>Go back</button>
            </div>
          )}

          <Textarea
            data-testid="consultation-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="E.g. Prefer weekday mornings. The brown spots started last month after a sprinkler change."
            rows={4}
            className="mt-3"
          />

          <Button
            data-testid="consultation-submit-button"
            onClick={submit}
            disabled={submitting || needsAck}
            className={`mt-4 w-full min-h-12 h-auto py-3 whitespace-normal text-sm sm:text-base font-semibold rounded-xl ${toneBtn}`}
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} /> Sending…
              </>
            ) : (
              <>
                <PhoneCall className="mr-2" size={18} /> REQUEST A GREENFLOW CONSULTATION
              </>
            )}
          </Button>
          <p className="mt-2 text-[11px] text-center text-slate-500">
            Your request is sent to askDr.Verdant@greenflowlandcare.com
          </p>
        </section>
      </main>
    </div>
  );
}
